import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default class ScrollTicker extends HTMLElement {
	constructor() {
		super();
		this.type = this.dataset.type
		if (this.type === "none") return;
		this.wrapper = this.querySelector(".scroll-ticker-wrapper")
		if (this.type == "mobile" && window.Zero.windowWidth >= 768) return;
		if (this.type == "desktop" && window.Zero.windowWidth <= 768) return;
		this.init();

		// Re-init on resize so item sizes, step, and clone counts update at breakpoints.
		// Width only: mobile in-app browsers (Instagram in particular) fire `resize` every time
		// their toolbar collapses or expands mid-scroll. Re-initing there rebuilds the timeline's
		// ScrollTrigger, and creating a ScrollTrigger forces a global refresh that restores a
		// recorded scroll position, which yanks the page down. Nothing below depends on viewport
		// height, so a height-only resize is never a reason to rebuild.
		let resizeTimer;
		let lastWidth = window.innerWidth;
		window.addEventListener('resize', () => {
			if (window.innerWidth === lastWidth) return;
			lastWidth = window.innerWidth;
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(() => {
				this.destroy();
				this.init();
			}, 200);
		});
	}

	destroy() {
		if (this._teardown) {
			this._teardown();
			this._teardown = null;
		}
	}

	setupDrag(element, onStart, onMove, onEnd, styleTarget = element) {
		let isDragging = false;
		let hasDragged = false;
		let startX = 0;
		let startY = 0;
		let activePointerId = null;
		const DRAG_THRESHOLD = 5;

		element.addEventListener('pointerdown', (e) => {
			isDragging = true;
			hasDragged = false;
			startX = e.clientX;
			startY = e.clientY;
			activePointerId = e.pointerId;
			styleTarget.style.cursor = 'grabbing';
			onStart(e);
		});

		element.addEventListener('pointermove', (e) => {
			if (!isDragging) return;
			if (!hasDragged) {
				const dx = Math.abs(e.clientX - startX);
				const dy = Math.abs(e.clientY - startY);
				if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
					hasDragged = true;
					element.setPointerCapture(activePointerId);
				}
			}
			if (hasDragged) onMove(e);
		});

		const endDrag = (e) => {
			if (!isDragging) return;
			isDragging = false;
			styleTarget.style.cursor = 'grab';
			onEnd(e);
		};

		element.addEventListener('pointerup', endDrag);
		element.addEventListener('pointercancel', endDrag);

		// Suppress click only when the pointer actually moved (drag), not on a tap/click
		element.addEventListener('click', (e) => {
			if (hasDragged) {
				e.preventDefault();
				e.stopPropagation();
				hasDragged = false;
			}
		}, true);

		styleTarget.style.cursor = 'grab';
		styleTarget.style.touchAction = 'none';
	}

	createMarquee(wrapper, tl) {
		const lineTicker = this.wrapper.querySelector(".line-ticker")
		const arcTicker  = this.wrapper.querySelector(".arc-ticker")

		if (lineTicker) {
			const tickerClone1 = lineTicker.cloneNode(true)
			const tickerClone2 = lineTicker.cloneNode(true)
			this.wrapper.appendChild(tickerClone1)
			this.wrapper.appendChild(tickerClone2)

			// Proxy object: scroll timeline animates this, not the DOM element
			const proxy = { xPercent: 0 };
			let dragXPercent    = 0;
			let dragStartXPercent = 0;
			let dragStartX      = 0;
			let velocity        = 0;
			let lastX           = 0;
			let lastTime        = 0;
			let inertiaTween    = null;

			tl.to(proxy, { xPercent: -33, duration: 20, ease: 'none' }, 0);

			const tickerFn = () => {
				gsap.set(wrapper, { xPercent: proxy.xPercent + dragXPercent });
			};
			gsap.ticker.add(tickerFn);

			// Teardown: used by destroy() before a resize re-init. The arc branch below has always
			// had one; without it here, every re-init leaked this branch's ScrollTrigger, its two
			// clones and its ticker callback, so the page grew taller on each resize.
			this._teardown = () => {
				gsap.ticker.remove(tickerFn);
				tl.kill();
				if (inertiaTween) inertiaTween.kill();
				tickerClone1.remove();
				tickerClone2.remove();
				gsap.set(wrapper, { clearProps: 'all' });
			};

			this.setupDrag(
				wrapper,
				(e) => {
					if (inertiaTween) inertiaTween.kill();
					dragStartX        = e.clientX;
					dragStartXPercent = dragXPercent;
					lastX             = e.clientX;
					lastTime          = Date.now();
					velocity          = 0;
				},
				(e) => {
					const now = Date.now();
					const dt  = now - lastTime || 1;
					velocity  = (e.clientX - lastX) / dt * 16;
					lastX     = e.clientX;
					lastTime  = now;

					const delta          = e.clientX - dragStartX;
					const containerWidth = this.getBoundingClientRect().width || 1;
					dragXPercent = dragStartXPercent + (delta / containerWidth) * 100;
				},
				() => {
					if (Math.abs(velocity) < 0.5) return;
					const vel            = { value: velocity };
					const containerWidth = this.getBoundingClientRect().width || 1;
					inertiaTween = gsap.to(vel, {
						value: 0,
						duration: 1.2,
						ease: 'power2.out',
						onUpdate: () => {
							dragXPercent += (vel.value / containerWidth) * 100;
						}
					});
				}
			);
		} else if (arcTicker) {
			const wrapSizeVw = 160; // matches $wrap_size in SCSS

			// Read the actual rendered item size from the DOM so spacing is always
			// correct regardless of breakpoint detection (isMobile can be unreliable).
			const origLis    = Array.from(arcTicker.querySelectorAll('li'));
			const itemSizePx = origLis[0]?.getBoundingClientRect().width || (0.08 * window.innerWidth);
			const itemSizeVw = itemSizePx / window.innerWidth * 100;

			// Each li is positioned at 12 o'clock (top) or 6 o'clock (bottom) in CSS.
			// transform-origin must point at the circle center relative to the item's top-left.
			// 12 o'clock: item top-left at (76vw, 0)      → origin = (itemHalf, wrapSize/2)
			// 6 o'clock:  item top-left at (76vw, 152vw)  → origin = (itemHalf, itemSize - wrapSize/2)
			const isBottom = arcTicker.classList.contains('arc-ticker--bottom');
			const itemOriginY = isBottom ? itemSizeVw - wrapSizeVw / 2 : wrapSizeVw / 2;
			const itemOrigin    = `${itemSizeVw / 2}vw ${itemOriginY}vw`;
			const orbitRadiusVw = wrapSizeVw / 2 - itemSizeVw / 2;

			// Compute step from actual item arc width so gap ≈ 1.5× item width at any size.
			// arcsin gives the half-angle subtended by the item at the orbit radius.
			const itemAngularWidth = 2 * Math.asin((itemSizeVw / 2) / orbitRadiusVw) * (180 / Math.PI);
			// Snap totalNeeded to an integer first, then derive step so it divides 360° evenly.
			// This guarantees a perfect circle — no overlap or gap at the seam.
			const totalNeeded = Math.round(360 / (itemAngularWidth * 1.15));
			const step = 360 / totalNeeded; // ~9° desktop (8vw), ~32° mobile (25vw)

			// arc-ticker rotates around its own centre (50% 50% = 80vw,80vw = circle centre).
			gsap.set(arcTicker, { transformOrigin: 'center' });

			// Stamp all original li items at their angles (JS owns rotation, not CSS).
			origLis.forEach((li, i) => {
				gsap.set(li, { rotation: i * step, transformOrigin: itemOrigin });
			});

			// Clone items to fill the rest of 360°, cycling through originals for variety.
			const clones = [];
			for (let i = origLis.length; i < totalNeeded; i++) {
				const clone = origLis[i % origLis.length].cloneNode(true);
				arcTicker.appendChild(clone);
				gsap.set(clone, { rotation: i * step, transformOrigin: itemOrigin });
				clones.push(clone);
			}

			// --- Scroll + drag ---
			// Proxy carries the scroll-driven rotation (preserves scrub easing).
			// Each frame we add dragRotation and write the total to arcTicker.
			const proxy = { rotation: 0 };
			let dragRotation      = 0;
			let dragStartRotation = 0;
			let dragStartAngle    = 0;
			let angularVelocity   = 0;
			let lastAngle         = 0;
			let lastTime          = 0;
			let inertiaTween      = null;

			tl.to(proxy, { rotation: 40, duration: 20, ease: 'none' }, 0);

			const tickerFn = () => {
				gsap.set(arcTicker, { rotation: proxy.rotation + dragRotation });
			};
			gsap.ticker.add(tickerFn);

			// --- Arrow controls ---
			// Each arrow spins the ticker by nudging dragRotation (same value the
			// drag interaction drives), so clicks compose cleanly with scroll + drag.
			const rightArrow  = this.querySelector('.ticker-arrow--left');
			const leftArrow = this.querySelector('.ticker-arrow--right');
			// One spin = a few items of rotation, regardless of any in-flight tween.
			const spinStep = step * 4;
			let spinTarget = 0;
			let spinTween = null;
			const spin = (deg) => {
				if (inertiaTween) inertiaTween.kill();
				// Accumulate onto the in-flight destination so rapid clicks keep adding
				// rotation; resync to the live value after a drag or once idle.
				const base = (spinTween && spinTween.isActive()) ? spinTarget : dragRotation;
				spinTarget = base + deg;
				if (spinTween) spinTween.kill();
				const obj = { value: dragRotation };
				spinTween = gsap.to(obj, {
					value: spinTarget,
					duration: 0.9,
					ease: 'power2.out',
					onUpdate: () => { dragRotation = obj.value; }
				});
			};
			const onLeft  = () => spin(-spinStep);
			const onRight = () => spin(spinStep);
			// Stop the arrow's pointer events from triggering the element-wide drag,
			// which would otherwise flag a "drag" and swallow the click.
			const stopDrag = (e) => e.stopPropagation();
			[leftArrow, rightArrow].forEach((btn) => btn?.addEventListener('pointerdown', stopDrag));
			leftArrow?.addEventListener('click', onLeft);
			rightArrow?.addEventListener('click', onRight);

			// Teardown: used by destroy() before a resize re-init.
			this._teardown = () => {
				gsap.ticker.remove(tickerFn);
				tl.kill();
				if (spinTween) spinTween.kill();
				leftArrow?.removeEventListener('click', onLeft);
				rightArrow?.removeEventListener('click', onRight);
				clones.forEach(c => c.remove());
				gsap.set(origLis, { clearProps: 'all' });
				gsap.set(arcTicker, { clearProps: 'all' });
			};

			// Circle centre in viewport coordinates:
			//   X: viewport midpoint (arc-ticker left:-30vw + 80vw = 50vw) ✓
			//   Y: arc-ticker top + 80vw (circle centre is always half the arc-ticker height down,
			//      regardless of whether the arc-ticker is anchored top:0 or bottom:0)
			const getAngle = (clientX, clientY) => {
				const cx = window.innerWidth / 2;
				const cy = arcTicker.getBoundingClientRect().top + (wrapSizeVw / 2 / 100) * window.innerWidth;
				return Math.atan2(clientY - cy, clientX - cx) * (180 / Math.PI);
			};

			this.setupDrag(
				this,
				(e) => {
					if (inertiaTween) inertiaTween.kill();
					dragStartAngle    = getAngle(e.clientX, e.clientY);
					dragStartRotation = dragRotation;
					lastAngle         = dragStartAngle;
					lastTime          = Date.now();
					angularVelocity   = 0;
				},
				(e) => {
					const currentAngle = getAngle(e.clientX, e.clientY);
					const now = Date.now();
					const dt  = now - lastTime || 1;
					angularVelocity = (currentAngle - lastAngle) / dt * 16;
					lastAngle       = currentAngle;
					lastTime        = now;
					dragRotation = dragStartRotation + (currentAngle - dragStartAngle);
				},
				() => {
					if (Math.abs(angularVelocity) < 0.2) return;
					const vel = { value: angularVelocity };
					inertiaTween = gsap.to(vel, {
						value: 0,
						duration: 1.5,
						ease: 'power2.out',
						onUpdate: () => { dragRotation += vel.value; }
					});
				},
				this.wrapper
			);
		}
	}

	init() {
		if (!this.wrapper) return;

		if (this.type == "mobile" && window.Zero.windowWidth >= 768) return;
		if (this.type == "desktop" && window.Zero.windowWidth <= 768) return;

		let scrollTl = gsap.timeline({
			scrollTrigger: {
				trigger: this.wrapper,
				pin: false,
				start: "top bottom",
				scrub: 1,
			}
		});

		this.createMarquee(this.wrapper, scrollTl)
	}
}
